// Third party imports
// FilterContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useReducer } from "react";

import { useLocation } from "react-router-dom";

// Utils
import { buildQueryString } from "utils/filtersDataGrid";

const FilterContext = createContext();
// Funzione per parsare i parametri della query dall'URL
function parseQueryString(queryString) {
  if (!queryString || queryString === "?") return {};
  // Rimuovi il punto interrogativo iniziale
  const query = queryString.substring(1);
  const vars = query.split("&");
  const params = {};
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split("=");
    const key = decodeURIComponent(pair[0]);
    // Handle + symbols in URL encoding by replacing them with %20 before decoding
    const value = pair.length > 1 ? decodeURIComponent(pair[1].replace(/\+/g, '%20')) : "";
    // Gestione degli array (es. teams[]=1&teams[]=2)
    if (key.endsWith("[]")) {
      const actualKey = key.substring(0, key.length - 2);
      if (!params[actualKey]) {
        params[actualKey] = [];
      }
      params[actualKey].push(value);
    } else {
      params[key] = value;
    }
  }
  return params;
}
const initialState = {
  filters: {},
  activeFilters: [],
  isModal: false,
};
function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.name]: action.value,
        },
      };
    case "REMOVE_FILTER":
      const newFilters = { ...state.filters };
      delete newFilters[action.name];
      return {
        ...state,
        filters: newFilters,
      };
    case "SET_ACTIVE_FILTERS":
      return {
        ...state,
        activeFilters: action.filters,
      };
    case "SET_IS_MODAL":
      return {
        ...state,
        isModal: action.isModal,
      };
    case "RESET_FILTERS":
      return {
        ...state,
        filters: {},
      };
    case "INIT_FILTERS_FROM_URL":
      return {
        ...state,
        filters: action.filters,
      };
    case "UPDATE_URL":
      return state;
    default:
      return state;
  }
}
export function FilterProvider({ children, navigate = null }) {
  const location = useLocation();
  const [state, dispatch] = useReducer(filterReducer, initialState);
  // Carica i filtri dall'URL all'inizializzazione
  useEffect(() => {
    const urlFilters = parseQueryString(location.search);
    if (Object.keys(urlFilters).length > 0) {
      dispatch({ type: "INIT_FILTERS_FROM_URL", filters: urlFilters });
    }
  }, [location.search]);
  // Side effect per l'aggiornamento dell'URL
  const updateUrl = useCallback(
    (filters) => {
      if (navigate && !state.isModal) {
        navigate(
          {
            search: `?${buildQueryString(filters)}`,
            hash: window.location.hash,
          },
          { replace: true }
        ); // Usiamo replace per non aggiungere nuove entry nella history
      }
    },
    [state.isModal, navigate]
  );
  const setFilter = useCallback(
    (name, value) => {
      dispatch({ type: "SET_FILTER", name, value });
      const newFilters = { ...state.filters, [name]: value };
      updateUrl(newFilters);
    },
    [state.filters, updateUrl]
  );
  const removeFilter = useCallback(
    (name) => {
      dispatch({ type: "REMOVE_FILTER", name });
      const newFilters = { ...state.filters };
      delete newFilters[name];
      updateUrl(newFilters);
    },
    [state.filters, updateUrl]
  );
  const setActiveFilters = useCallback((filters) => {
    dispatch({ type: "SET_ACTIVE_FILTERS", filters });
  }, []);
  const setIsModal = useCallback((isModal) => {
    dispatch({ type: "SET_IS_MODAL", isModal });
  }, []);
  const resetFilters = useCallback(() => {
    dispatch({ type: "RESET_FILTERS" });
    if (navigate && !state.isModal) {
      navigate(
        {
          search: "",
          hash: window.location.hash,
        },
        { replace: true }
      );
    }
  }, [state.isModal, navigate]);
  const value = {
    filters: state.filters,
    activeFilters: state.activeFilters,
    isModal: state.isModal,
    setFilter,
    removeFilter,
    setActiveFilters,
    setIsModal,
    resetFilters,
    getQueryParams: () => state.filters,
  };
  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}
export const useFilters = () => {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error("useFilters must be used within a FilterProvider");
  }
  return context;
};
